import type { BlockType, EmailBlock, SocialLink } from '../HtmlEditor.types';

export const DEFAULT_TITLE_TEXT = 'Your title here for {{user}},';

export const SOCIAL_NETWORK_OPTIONS = [
  { id: 'facebook', label: 'Facebook', glyph: 'f', url: 'https://www.facebook.com' },
  { id: 'instagram', label: 'Instagram', glyph: '📷', url: 'https://www.instagram.com' },
  { id: 'linkedin', label: 'LinkedIn', glyph: 'in', url: 'https://www.linkedin.com' },
  { id: 'x', label: 'X', glyph: '𝕏', url: 'https://www.x.com' },
  { id: 'youtube', label: 'YouTube', glyph: '▶', url: 'https://www.youtube.com' },
  { id: 'tiktok', label: 'TikTok', glyph: '♪', url: 'https://www.tiktok.com' },
] as const;

export function getSocialNetworkMeta(networkId: string) {
  return SOCIAL_NETWORK_OPTIONS.find((n) => n.id === networkId);
}

export const DEFAULT_SOCIAL_LINKS: SocialLink[] = [
  { network: 'facebook', url: 'https://www.facebook.com' },
  { network: 'instagram', url: 'https://www.instagram.com' },
  { network: 'linkedin', url: 'https://www.linkedin.com' },
  { network: 'x', url: 'https://www.x.com' },
];
export const DEFAULT_BUTTON_HREF = 'https://audiencerate.com';

export const DISABLED_BLOCK_TYPES: BlockType[] = ['video', 'logo', 'nav'];

export const ED_BLOCKS: { type: BlockType; label: string; glyph: string }[] = [
  { type: 'title', label: 'Title', glyph: 'T' },
  { type: 'text', label: 'Text', glyph: '¶' },
  { type: 'image', label: 'Image', glyph: '🖼' },
  { type: 'video', label: 'Video', glyph: '▶' },
  { type: 'button', label: 'Button', glyph: '◼' },
  { type: 'dynamic', label: 'Dynamic', glyph: '{ }' },
  { type: 'logo', label: 'Logo', glyph: 'LOGO' },
  { type: 'social', label: 'Social', glyph: '◎' },
  { type: 'html', label: 'HTML', glyph: '</>' },
  { type: 'divider', label: 'Divider', glyph: '—' },
  // { type: 'product', label: 'Product', glyph: '👕' },
  { type: 'nav', label: 'Navigation', glyph: '☰' },
  { type: 'spacer', label: 'Spacer', glyph: '↕' },
];

export function isBlockTypeEnabled(type: BlockType): boolean {
  return !DISABLED_BLOCK_TYPES.includes(type);
}

export const PALETTE_BLOCKS = ED_BLOCKS.filter((b) => isBlockTypeEnabled(b.type));

let blockIdCounter = 1000;

export function createBlockId(): string {
  blockIdCounter += 1;
  return `b${blockIdCounter}`;
}

export function resetBlockIdCounter(maxId = 1000): void {
  blockIdCounter = maxId;
}

export function newBlock(type: BlockType, extra: Partial<EmailBlock> = {}): EmailBlock {
  const id = createBlockId();
  const base = {
    id,
    type,
    align: 'left' as const,
    width: 100,
    paddingTop: 12,
    paddingRight: 24,
    paddingBottom: 12,
    paddingLeft: 24,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    bg: 'transparent',
    bgImage: '',
    ...extra,
  };

  switch (type) {
    case 'title':
      return {
        ...base,
        type: 'title',
        text: DEFAULT_TITLE_TEXT,
        size: 30,
        color: '#0F172A',
        font: 'Arial',
        weight: 700,
        lineHeight: 1.2,
        paddingTop: 20,
        paddingBottom: 16,
      };
    case 'text':
      return {
        ...base,
        type: 'text',
        text: 'Start writing your content. Click on the text to edit it inline.',
        size: 14,
        color: '#334155',
        font: 'Arial',
        weight: 400,
        lineHeight: 1.6,
      };
    case 'image': {
      const imageExtra = extra as Partial<Extract<EmailBlock, { type: 'image' }>>;
      return {
        ...base,
        type: 'image',
        src: typeof imageExtra.src === 'string' ? imageExtra.src : '',
        placeholder: typeof imageExtra.placeholder === 'string' ? imageExtra.placeholder : '🌅',
        height: typeof imageExtra.height === 'number' ? imageExtra.height : 220,
        align: imageExtra.align ?? 'center',
      };
    }
    case 'video':
      return { ...base, type: 'video', thumb: '', label: 'Video block', align: 'center', height: 180 };
    case 'button':
      return {
        ...base,
        type: 'button',
        text: 'Call to action',
        href: DEFAULT_BUTTON_HREF,
        btnBg: '#0F172A',
        btnColor: '#FFFFFF',
        radius: 6,
        align: 'center',
        size: 14,
        paddingTop: 14,
        paddingBottom: 14,
      };
    case 'dynamic':
      return { ...base, type: 'dynamic', source: 'audience.first_name', fallback: 'there' };
    case 'logo':
      return { ...base, type: 'logo', text: 'YOUR LOGO', align: 'center', size: 18, color: '#0F172A', font: 'Arial', weight: 800 };
    case 'social':
      return { ...base, type: 'social', links: DEFAULT_SOCIAL_LINKS.map((link) => ({ ...link })), align: 'center' };
    case 'html': {
      const htmlExtra = extra as Partial<Extract<EmailBlock, { type: 'html' }>>;
      return {
        ...base,
        type: 'html',
        code: typeof htmlExtra.code === 'string' ? htmlExtra.code : '<p style="font-family:Arial">Custom HTML</p>',
      };
    }
    case 'divider':
      return { ...base, type: 'divider', color: '#E2E8F0', thickness: 1, paddingTop: 8, paddingBottom: 8 };
    case 'product':
      return { ...base, type: 'product', name: 'Product name', price: '€19,90', img: '👕', align: 'center' };
    // case 'nav':
    //   return { ...base, type: 'nav', items: ['Home', 'Shop', 'About', 'Contact'], align: 'center', color: '#0F172A' };
    case 'spacer':
      return { ...base, type: 'spacer', height: 24 };
    default:
      return { ...base, type: 'text', text: '', size: 14, color: '#334155', font: 'Arial', weight: 400, lineHeight: 1.6 };
  }
}

export function starterTemplate(): EmailBlock[] {
  const title = newBlock('title');
  const intro = newBlock('text');
  const cta = newBlock('button');
  const divider = newBlock('divider');
  const footer = newBlock('text');

  return [
    title,
    intro.type === 'text'
      ? {
          ...intro,
          text: 'Start your newsletter with a compelling intro. Click on this text to edit it. You can use {{name}} and other variables to personalize each email.',
        }
      : intro,
    cta.type === 'button' ? { ...cta, text: 'Call to action' } : cta,
    divider,
    footer.type === 'text'
      ? {
          ...footer,
          text: 'helpytravel · Piazza Libertà 5, Bassano del Grappa, VI, Italia\nThis email was sent via {{EMAIL}}. You received it because you subscribed to our newsletter.',
          size: 12,
          color: '#64748B',
          align: 'center',
        }
      : footer,
  ];
}

export const BLOCK_LABELS: Record<BlockType, string> = {
  title: 'Headline',
  text: 'Text',
  image: 'Image',
  video: 'Video',
  button: 'Button',
  dynamic: 'Dynamic content',
  logo: 'Logo',
  social: 'Social',
  html: 'HTML',
  divider: 'Divider',
  product: 'Product',
  nav: 'Navigation',
  spacer: 'Spacer',
};
